import graphqlService from '../../services/graphqlService'
import {
  mutationCreateUserColumnPreference,
  mutationUpdateUserColumnPreference,
  queryUserColumnPreferences,
  mapQueryUserColumnPreferences,
  UserColumnPreferenceParams,
  UpdateUserColumnPreferenceParams,
  ColumnConfig
} from '../../services/userPreferenceQueries'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

// Types
export interface UserColumnPreference {
  id: string
  userEmail: string
  preferenceType: string
  table: string
  columnsConfig: ColumnConfig[]
  updateTimestamp?: string
}

export interface UserPreferencesState {
  columnPreferences: UserColumnPreference[]
  loading: boolean
  error: string | null
}

// Initial state
export const initialState: UserPreferencesState = {
  columnPreferences: [],
  loading: false,
  error: null
}

// Async thunks
export const ACTION_CREATE_USER_COLUMN_PREFERENCE = 'userPreferences/createColumnPreference'
export const createUserColumnPreference = createAsyncThunk(
  ACTION_CREATE_USER_COLUMN_PREFERENCE,
  async (params: UserColumnPreferenceParams, { rejectWithValue }) => {
    try {
      const mutation = mutationCreateUserColumnPreference(params)
      const result = await graphqlService.execute(mutation)
      return {
        ...result.data.createUserPreference,
        ...params // Include the original params for state update
      }
    } catch (error: any) {
      return rejectWithValue({
        statusMessage: error?.errors?.[0]?.message || 'Failed to create user column preference'
      })
    }
  }
)

export const ACTION_UPDATE_USER_COLUMN_PREFERENCE = 'userPreferences/updateColumnPreference'
export const updateUserColumnPreference = createAsyncThunk(
  ACTION_UPDATE_USER_COLUMN_PREFERENCE,
  async (params: UpdateUserColumnPreferenceParams, { rejectWithValue }) => {
    try {
      const mutation = mutationUpdateUserColumnPreference(params)
      const result = await graphqlService.execute(mutation)
      return {
        ...result.data.updateUserPreference,
        userPreferenceId: params.userPreferenceId, // Include the ID for state update
        table: params.table,
        columnsConfig: params.columnsConfig
      }
    } catch (error: any) {
      return rejectWithValue({
        statusMessage: error?.errors?.[0]?.message || 'Failed to update user column preference'
      })
    }
  }
)

export const ACTION_FETCH_USER_COLUMN_PREFERENCES = 'userPreferences/fetchColumnPreferences'
export const fetchUserColumnPreferences = createAsyncThunk(
  ACTION_FETCH_USER_COLUMN_PREFERENCES,
  async (params: { userEmail?: string; table?: string }, { rejectWithValue }) => {
    try {
      const query = queryUserColumnPreferences(params.userEmail, params.table)
      const result = await graphqlService.execute(query)
      return mapQueryUserColumnPreferences(result)
    } catch (error: any) {
      return rejectWithValue({
        statusMessage: error?.errors?.[0]?.message || 'Failed to fetch user column preferences'
      })
    }
  }
)

// Slice
const userPreferencesSlice = createSlice({
  name: 'userPreferences',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    resetUserPreferences: () => initialState
  },
  extraReducers: (builder) => {
    // Create user column preference
    builder
      .addCase(createUserColumnPreference.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createUserColumnPreference.fulfilled, (state, action) => {
        state.loading = false
        const newPreference: UserColumnPreference = {
          id: action.payload.userPreferenceConfigId,
          userEmail: action.payload.userEmail,
          preferenceType: 'TABLE_COLUMN_SETTINGS',
          table: action.payload.table,
          columnsConfig: action.payload.columnsConfig
        }
        state.columnPreferences.push(newPreference)
      })
      .addCase(createUserColumnPreference.rejected, (state, action: any) => {
        state.loading = false
        state.error = action.payload?.statusMessage || 'Failed to create preference'
      })

    // Update user column preference
    builder
      .addCase(updateUserColumnPreference.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateUserColumnPreference.fulfilled, (state, action) => {
        state.loading = false
        const index = state.columnPreferences.findIndex(
          (pref) => pref.id === action.payload.userPreferenceId
        )
        if (index !== -1) {
          state.columnPreferences[index] = {
            ...state.columnPreferences[index],
            id: action.payload.userPreferenceConfigId, // Update with the returned ID
            table: action.payload.table,
            columnsConfig: action.payload.columnsConfig
          }
        }
      })
      .addCase(updateUserColumnPreference.rejected, (state, action: any) => {
        state.loading = false
        state.error = action.payload?.statusMessage || 'Failed to update preference'
      })

    // Fetch user column preferences
    builder
      .addCase(fetchUserColumnPreferences.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUserColumnPreferences.fulfilled, (state, action) => {
        state.loading = false
        state.columnPreferences = action.payload
      })
      .addCase(fetchUserColumnPreferences.rejected, (state, action: any) => {
        state.loading = false
        state.error = action.payload?.statusMessage || 'Failed to fetch preferences'
      })
  }
})

export const { clearError, resetUserPreferences } = userPreferencesSlice.actions

export default userPreferencesSlice.reducer
